import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";
import { Delete, Edit, Add, CloudUpload } from "@mui/icons-material";
import { motion } from "framer-motion";
import { FaBook, FaFilePdf, FaFileWord, FaFileExcel, FaLink, FaVideo } from "react-icons/fa";

const AdminResources = () => {
  const [resources, setResources] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSeverity, setFeedbackSeverity] = useState("success");
  const [currentResource, setCurrentResource] = useState(null);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [fileType, setFileType] = useState("");
  const [fileUrl, setFileUrl] = useState("");

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
      "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d9ff" },
    "& .MuiSelect-icon": { color: "rgba(255, 255, 255, 0.7)" },
  };

  const categories = [
    { value: "past-papers", label: "Past Papers" },
    { value: "notes", label: "Lecture Notes" },
    { value: "tutorials", label: "Tutorials" },
    { value: "projects", label: "Projects" },
    { value: "other", label: "Other" },
  ];

  const fileTypes = [
    { value: "pdf", label: "PDF", icon: <FaFilePdf color="#ef4444" /> },
    { value: "doc", label: "Word Document", icon: <FaFileWord color="#3b82f6" /> },
    { value: "xls", label: "Excel", icon: <FaFileExcel color="#22c55e" /> },
    { value: "video", label: "Video", icon: <FaVideo color="#a855f7" /> },
    { value: "link", label: "External Link", icon: <FaLink color="#00d9ff" /> },
  ];

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const snapshot = await firestore
        .collection("resources")
        .orderBy("createdAt", "desc")
        .get();
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setResources(data);
    } catch (error) {
      console.error("Error fetching resources:", error);
    } finally {
      setLoading(false);
    }
  };

  const showFeedback = (message, severity = "success") => {
    setFeedbackMessage(message);
    setFeedbackSeverity(severity);
    setFeedbackOpen(true);
  };

  const openPopup = () => setOpen(true);
  const closePopup = () => {
    setOpen(false);
    resetForm();
  };

  const openEditPopup = (resource) => {
    setCurrentResource(resource);
    setTitle(resource.title);
    setDescription(resource.description);
    setCategory(resource.category);
    setFileType(resource.fileType);
    setFileUrl(resource.fileUrl);
    setEditOpen(true);
  };

  const closeEditPopup = () => {
    setEditOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("");
    setFileType("");
    setFileUrl("");
    setCurrentResource(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const newResource = {
        title,
        description,
        category,
        fileType,
        fileUrl,
        createdAt: new Date(),
      };
      const docRef = await firestore.collection("resources").add(newResource);
      setResources([{ id: docRef.id, ...newResource }, ...resources]);
      closePopup();
      showFeedback("Resource added successfully!");
    } catch (error) {
      console.error("Error adding resource:", error);
      showFeedback("Error adding resource", "error");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedResource = {
        title,
        description,
        category,
        fileType,
        fileUrl,
        updatedAt: new Date(),
      };
      await firestore.collection("resources").doc(currentResource.id).update(updatedResource);
      const updatedResources = resources.map((resource) =>
        resource.id === currentResource.id ? { ...resource, ...updatedResource } : resource
      );
      setResources(updatedResources);
      closeEditPopup();
      showFeedback("Resource updated successfully!");
    } catch (error) {
      console.error("Error updating resource:", error);
      showFeedback("Error updating resource", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this resource?")) return;
    try {
      await firestore.collection("resources").doc(id).delete();
      setResources(resources.filter((resource) => resource.id !== id));
      showFeedback("Resource deleted successfully!");
    } catch (error) {
      console.error("Error deleting resource:", error);
      showFeedback("Error deleting resource", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
          backgroundSize: "cover",
        }}
      >
        <CircularProgress sx={{ color: "#00d9ff" }} size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "15px",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaBook size={30} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>
                Manage Resources
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Upload and manage member resources
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Action Buttons */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Box sx={{ display: "flex", gap: 2, my: 3 }}>
            <Button
              onClick={openPopup}
              variant="contained"
              startIcon={<Add />}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1.2,
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                fontWeight: 600,
                "&:hover": { background: "linear-gradient(135deg, #2563eb, #1d4ed8)" },
              }}
            >
              Add Resource
            </Button>
          </Box>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Card sx={{ borderRadius: "15px", ...darkGlassStyle, px: 3, py: 2 }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#00d9ff" }}>
                {resources.length}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Total Resources
              </Typography>
            </Card>
            {categories.map((cat) => (
              <Card key={cat.value} sx={{ borderRadius: "15px", ...darkGlassStyle, px: 3, py: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#22c55e" }}>
                  {resources.filter((r) => r.category === cat.value).length}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  {cat.label}
                </Typography>
              </Card>
            ))}
          </Box>
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
          <Card sx={{ borderRadius: "20px", ...darkGlassStyle, overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.1))" }}>
                    {["Title", "Category", "Type", "Date Added", "Actions"].map((header) => (
                      <TableCell
                        key={header}
                        sx={{ color: "#00d9ff", fontWeight: 700, fontSize: "15px", borderBottom: "1px solid rgba(0, 217, 255, 0.2)" }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.length > 0 ? (
                    resources.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((resource) => (
                      <TableRow
                        key={resource.id}
                        sx={{ "&:hover": { background: "rgba(0, 217, 255, 0.05)" }, borderBottom: "1px solid rgba(255,255,255,0.05)" }}
                      >
                        <TableCell sx={{ color: "white", fontSize: "14px", fontWeight: 600 }}>{resource.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={categories.find((c) => c.value === resource.category)?.label || resource.category}
                            size="small"
                            sx={{ background: "rgba(0, 217, 255, 0.2)", color: "#00d9ff" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {fileTypes.find((f) => f.value === resource.fileType)?.icon}
                            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>
                              {fileTypes.find((f) => f.value === resource.fileType)?.label || resource.fileType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>
                          {resource.createdAt?.toDate?.()?.toLocaleDateString() || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => openEditPopup(resource)}
                              sx={{
                                borderRadius: "8px",
                                minWidth: "auto",
                                px: 1.5,
                                background: "rgba(16, 185, 129, 0.2)",
                                border: "1px solid #10b981",
                                color: "#10b981",
                                "&:hover": { background: "rgba(16, 185, 129, 0.3)" },
                              }}
                            >
                              <Edit fontSize="small" />
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleDelete(resource.id)}
                              sx={{
                                borderRadius: "8px",
                                minWidth: "auto",
                                px: 1.5,
                                background: "rgba(239, 68, 68, 0.2)",
                                border: "1px solid #ef4444",
                                color: "#ef4444",
                                "&:hover": { background: "rgba(239, 68, 68, 0.3)" },
                              }}
                            >
                              <Delete fontSize="small" />
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}>
                        No resources added yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={resources.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ color: "white", borderTop: "1px solid rgba(0, 217, 255, 0.2)", "& .MuiTablePagination-selectIcon": { color: "white" } }}
            />
          </Card>
        </motion.div>

        {/* Add Dialog */}
        <Dialog open={open} onClose={closePopup} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloudUpload sx={{ color: "white" }} />
            </Box>
            Add New Resource
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth sx={inputStyle} />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  sx={inputStyle}
                />
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="Category"
                    required
                    MenuProps={{
                      PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } },
                    }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>File Type</InputLabel>
                  <Select
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    label="File Type"
                    required
                    MenuProps={{
                      PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } },
                    }}
                  >
                    {fileTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="File URL / Link"
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                  required
                  fullWidth
                  sx={inputStyle}
                  placeholder="https://drive.google.com/... or direct link"
                />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  Upload files to Google Drive or Dropbox and paste the share link here
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                    fontWeight: 600,
                    fontSize: "1rem",
                    "&:hover": { background: "linear-gradient(135deg, #2563eb, #1d4ed8)" },
                  }}
                >
                  Add Resource
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onClose={closeEditPopup} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #10b981, #059669)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Edit sx={{ color: "white" }} />
            </Box>
            Edit Resource
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleEditSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth sx={inputStyle} />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  sx={inputStyle}
                />
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="Category"
                    required
                    MenuProps={{
                      PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } },
                    }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>File Type</InputLabel>
                  <Select
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    label="File Type"
                    required
                    MenuProps={{
                      PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } },
                    }}
                  >
                    {fileTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="File URL / Link" value={fileUrl} onChange={(e) => setFileUrl(e.target.value)} required fullWidth sx={inputStyle} />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    fontWeight: 600,
                    fontSize: "1rem",
                    "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" },
                  }}
                >
                  Update Resource
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Feedback Snackbar */}
        <Snackbar open={feedbackOpen} autoHideDuration={4000} onClose={() => setFeedbackOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={() => setFeedbackOpen(false)} severity={feedbackSeverity} sx={{ width: "100%", borderRadius: "12px" }}>
            {feedbackMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AdminResources;
